import { lazy, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, useLocation } from 'react-router';
import { AppShell } from '@/components/layout/AppShell';
import { EmptyState } from '@/components/ui';
import { useAuthStore } from '@/stores/auth.store';
import { homeFor } from './navigation';

const LoginPage = lazy(() => import('@/features/auth/LoginPage'));
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'));
const InvoicesPage = lazy(() => import('@/features/invoices/InvoicesPage'));
const CollectionsPage = lazy(() => import('@/features/collections/CollectionsPage'));
const TradersPage = lazy(() => import('@/features/traders/TradersPage'));
const TraderProfilePage = lazy(() => import('@/features/traders/TraderProfilePage'));
const ProductsPage = lazy(() => import('@/features/products/ProductsPage'));
const NewProductPage = lazy(() => import('@/features/products/NewProductPage'));
const InventoryPage = lazy(() => import('@/features/inventory/InventoryPage'));
const PurchasesPage = lazy(() => import('@/features/purchases/PurchasesPage'));
const NewPurchasePage = lazy(() => import('@/features/purchases/NewPurchasePage'));
const PurchaseDetailPage = lazy(() => import('@/features/purchases/PurchaseDetailPage'));
const PurchasePaymentPage = lazy(() => import('@/features/purchases/PurchasePaymentPage'));
const SuppliersPage = lazy(() => import('@/features/suppliers/SuppliersPage'));
const SupplierProfilePage = lazy(() => import('@/features/suppliers/SupplierProfilePage'));
const ReturnsPage = lazy(() => import('@/features/returns/ReturnsPage'));
const WalletsPage = lazy(() => import('@/features/wallets/WalletsPage'));
const RegionsPage = lazy(() => import('@/features/regions/RegionsPage'));
const TeamPage = lazy(() => import('@/features/team/TeamPage'));
const SurveyPage = lazy(() => import('@/features/survey/SurveyPage'));
const VisitsPage = lazy(() => import('@/features/visits/VisitsPage'));
const PlansPage = lazy(() => import('@/features/plans/PlansPage'));
const ReportsPage = lazy(() => import('@/features/reports/ReportsPage'));
const UsersPage = lazy(() => import('@/features/users/UsersPage'));
const AppOrdersPage = lazy(() => import('@/features/app-orders/AppOrdersPage'));
const NotificationsPage = lazy(() => import('@/features/notifications/NotificationsPage'));
const SettingsPage = lazy(() => import('@/features/settings/SettingsPage'));

function RequireAuth({ children }: { children: ReactNode }) {
  const status = useAuthStore((s) => s.status);
  const location = useLocation();
  if (status === 'anonymous') return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return children;
}

/** `/` is the dashboard for managers; other roles are sent to their first allowed page. */
function Home() {
  const user = useAuthStore((s) => s.user)!;
  const home = homeFor(user);
  return home === '/' ? <DashboardPage /> : <Navigate to={home} replace />;
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Home /> },
      { path: 'invoices', element: <InvoicesPage /> },
      { path: 'collections', element: <CollectionsPage /> },
      { path: 'traders', element: <TradersPage /> },
      { path: 'traders/:id', element: <TraderProfilePage /> },
      { path: 'products', element: <ProductsPage /> },
      { path: 'products/new', element: <NewProductPage /> },
      { path: 'inventory', element: <InventoryPage /> },
      { path: 'purchases', element: <PurchasesPage /> },
      { path: 'purchases/new', element: <NewPurchasePage /> },
      { path: 'purchases/:id', element: <PurchaseDetailPage /> },
      { path: 'purchases/:id/pay', element: <PurchasePaymentPage /> },
      { path: 'suppliers', element: <SuppliersPage /> },
      { path: 'suppliers/:id', element: <SupplierProfilePage /> },
      { path: 'returns', element: <ReturnsPage /> },
      { path: 'app-orders', element: <AppOrdersPage /> },
      { path: 'wallets', element: <WalletsPage /> },
      { path: 'regions', element: <RegionsPage /> },
      { path: 'team', element: <TeamPage /> },
      { path: 'survey', element: <SurveyPage /> },
      { path: 'visits', element: <VisitsPage /> },
      { path: 'plans', element: <PlansPage /> },
      { path: 'reports', element: <ReportsPage /> },
      { path: 'users', element: <UsersPage /> },
      { path: 'notifications', element: <NotificationsPage /> },
      { path: 'settings', element: <SettingsPage /> },
      { path: 'no-access', element: <EmptyState title="لا توجد صفحات متاحة لحسابك" hint="الحسابات الميدانية تستخدم تطبيق الموبايل" /> },
      { path: '*', element: <EmptyState title="الصفحة غير موجودة" /> },
    ],
  },
]);
